import { useNavigate, useSearchParams } from 'react-router-dom';
import React, { useLayoutEffect, useEffect, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ScrollView, YStack } from 'tamagui';
import { Strings } from '../../constants/Strings';
import { showErrorMessage, showSuccessMessage } from '../../utils/messageToast';
// import { useCreateGroupEventMutation, useUpdateGroupEventMutation } from '@/services/events/eventsHooks';

import {
    CobuContainer,
    CobuStack,
    CobuSpacer,
    CobuButton,
    CobuInput,
    CobuDateTimePicker,
    CobuText,
    CobuImagePicker,
    CobuHeader,
    CobuBackButton,
} from '@cobu/ui';
import CobuGroupPicker from '../../components/groups/CobuGroupPicker';
import { IconSymbol } from '../../components/ui/IconSymbol';
import { createGroupEventSchema, groupEventDefaultValues, type GroupEventFormValues } from '../../schemas/groupEventSchema';

// Schema and defaults now come from ../../schemas/groupEventSchema
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { momentToTimeZone, parseToTimeZone } from '../../utils/dateTime';
import { useCreateGroupEventMutation, useUpdateGroupEventMutation } from '../../hooks/events';
import { logger } from '../../utils/logger';

export default function CreateGroupEventPage() {
    const navigate = useNavigate();
    const strings = Strings.get();
    const [searchParams] = useSearchParams();
    // Wire up backend mutations via local hooks (wrapping api-client)
    const createGroupEventMutation = useCreateGroupEventMutation();
    const updateGroupEventMutation = useUpdateGroupEventMutation();
    const { user } = useSelector((state: RootState) => state.auth);

    // Check if we're in edit mode
    const isEditMode = !!searchParams.get('editMode') && !!searchParams.get('eventId');
    const eventId = searchParams.get('eventId') || '';
    const hasInitialized = useRef(false);

    const groupEventSchema = useMemo(() => createGroupEventSchema(), []);
    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        watch,
        reset,
    } = useForm<GroupEventFormValues>({
        resolver: zodResolver(groupEventSchema),
        defaultValues: groupEventDefaultValues,
        mode: 'onChange',
    });
    const [removedImage, setRemovedImage] = React.useState(false);

    const watchedValues = watch();
    const isSubmitting = createGroupEventMutation.isPending || updateGroupEventMutation.isPending;

    const handleGoBack = () => {
        navigate(-1);
    };

    // useLayoutEffect(() => {
    //   // navigation.setOptions({
    //     headerShown: true,
    //     header: () => (
    //       <CobuHeader
    //         title={isEditMode ? strings.events.create.headerTitles.edit : strings.events.create.headerTitles.create}
    //         renderLeft={() => <CobuBackButton onPress={() => navigate(-1)} />}
    //       />
    //     ),
    //   // });
    // }, [isEditMode, strings]);

    // Helper function to get string value from params
    const getParamValue = (key: string): string => searchParams.get(key) || '';


    // Pre-populate form when in edit mode or when group info is provided
    useEffect(() => {
        // Pre-populate all event data in edit mode
        if (isEditMode && !hasInitialized.current && eventId) {
            const eventData = {
                title: getParamValue('title'),
                description: getParamValue('description'),
                locationString: getParamValue('locationString'),
                whatToBring: getParamValue('whatToBring'),
                maxEventAttendees: getParamValue('maxEventAttendees'),
                maxGuests: getParamValue('maxGuests'),
                startTime: searchParams.get('startTime') ? new Date(parseToTimeZone(getParamValue('startTime'), user?.building?.timezone || '')) : new Date(),
                group: {
                    value: getParamValue('groupId'),
                    label: getParamValue('groupName'),
                },
                // File input should be empty; preview handled via initialUri on the picker
                imageFile: null,
            };

            reset(eventData);
            hasInitialized.current = true;
        }
        // Pre-populate group info in create mode when group params are provided
        else if (!isEditMode && !hasInitialized.current && searchParams.get('groupId') && searchParams.get('groupName')) {
            const groupData = {
                group: {
                    value: getParamValue('groupId'),
                    label: getParamValue('groupName'),
                },
            };

            reset(groupData);
            hasInitialized.current = true;
        }

        // Reset initialization flag when switching from edit to create mode
        if (!isEditMode && hasInitialized.current && !searchParams.get('groupId')) {
            hasInitialized.current = false;
        }
    }, [isEditMode, eventId, searchParams]);

    const onSubmit = async (data: GroupEventFormValues) => {
        try {
            // Validate group selection in create mode
            if (!isEditMode && (!data.group || !data.group.value || !data.group.label)) {
                showErrorMessage(`${strings.common.error.title}: ${strings.events.create.errors.selectGroup}`);
                return;
            }

            if (!data.startTime) {
                showErrorMessage(`${strings.common.error.title}: ${strings.events.create.errors.selectDateTime}`);
                return;
            }

            data.startTime = momentToTimeZone(data.startTime, user?.building?.timezone || '')

            if (isEditMode && eventId) {
                // Update existing event
                await updateGroupEventMutation.mutateAsync({
                    eventId,
                    groupId: data.group?.value || '',
                    eventData: {
                        ...data,
                        imageFile: (data.imageFile as any as File | null) || null,
                        removeImage: removedImage && !(data.imageFile as any as File | null),
                    },
                    currentEventDate: getParamValue('currentEventDate'),
                });

                showSuccessMessage(`${strings.events.create.success.title}: ${strings.events.create.success.eventUpdated}`);
                handleGoBack();
            } else {
                // Create new event
                await createGroupEventMutation.mutateAsync({
                    groupId: data.group?.value || '',
                    eventData: {
                        ...data,
                        imageFile: (data.imageFile as any as File | null) || null,
                    },
                    creator: user?.uuid || '',
                });
                analyticsService.trackEvent(AnalyticsEvent.CREATE_GROUP_EVENT, { user, groupData: data.group, eventData: data });
                showSuccessMessage(`${strings.events.create.success.title}: ${strings.events.create.success.eventCreated}`);
                handleGoBack();
            }
        } catch (error) {
            logger.error('Error saving group event:', error);

            const errorMessage = (isEditMode ? strings.events.create.errors.updateFailed : strings.events.create.errors.createFailed);
            showErrorMessage(`${strings.common.error.title}: ${errorMessage}`);
        }
    };

    return (
        <CobuContainer>
            <CobuHeader
                title={isEditMode ? strings.events.create.headerTitles.edit : strings.events.create.headerTitles.create}
                renderLeft={() => <CobuBackButton onPress={() => navigate(-1)} />}
            />
            <ScrollView>
                <CobuStack p="$4" gap="$4">

                    {/* Image Upload */}
                    <Controller
                        control={control}
                        name="imageFile"
                        render={({ field: { value, onChange } }) => (
                            <CobuImagePicker
                                value={value as any as File | null}
                                onChange={(file: File | null) => {
                                    onChange(file as any);
                                    if (file) setRemovedImage(false);
                                }}
                                onRemove={() => setRemovedImage(true)}
                                placeholder={strings.common.placeholders.gatheringPhotoOptional}
                                removeLabel={strings.ui.imagePicker.removePhoto}
                                heightToken="$56"
                                initialUri={isEditMode ? (searchParams.get('imageUri') || '') : undefined}
                            />
                        )}
                    />

                    {/* Group Selection - Only show in create mode */}
                    {true && (
                        <Controller
                            control={control}
                            name="group"
                            render={({ field: { onChange, value } }) => (
                                <CobuGroupPicker
                                    label={strings.events.create.labels.selectGroup}
                                    value={value}
                                    onChange={onChange}
                                    error={errors.group?.message}
                                    placeholder={strings.events.create.placeholders.chooseGroup}
                                />
                            )}
                        />
                    )}

                    {/* Title Field */}
                    <Controller
                        control={control}
                        name="title"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CobuInput
                                label={strings.events.create.labels.gatheringName}
                                placeholder={strings.events.create.placeholders.gatheringName}
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={errors.title?.message}
                                autoCapitalize="sentences"
                                p="$3"
                                fos="$3"
                            />
                        )}
                    />

                    {/* Date/Time Picker */}
                    <Controller
                        control={control}
                        name="startTime"
                        render={({ field: { onChange, value } }) => (
                            <CobuDateTimePicker
                                label={strings.events.create.labels.gatheringDateTime}
                                value={value}
                                onChange={onChange}
                                error={errors.startTime?.message}
                                placeholder={strings.events.create.placeholders.dateTime}
                                mode="datetime"
                                minimumDate={new Date(Date.now() + 60 * 60 * 1000)} // 1 hour from now
                                maximumDate={new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)} // 3 months from now
                                selectDateLabel={strings.ui.dateTimePicker.selectDate}
                                selectTimeLabel={strings.ui.dateTimePicker.selectTime}
                                selectDateTimeLabel={strings.ui.dateTimePicker.selectDateTime}
                                chooseDateLabel={strings.ui.dateTimePicker.chooseDate}
                                chooseTimeLabel={strings.ui.dateTimePicker.chooseTime}
                                cancelLabel={strings.ui.dateTimePicker.cancel}
                                confirmLabel={strings.ui.dateTimePicker.confirm}
                            />
                        )}
                    />

                    {/* Location Field */}
                    <Controller
                        control={control}
                        name="locationString"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CobuInput
                                label={strings.events.create.labels.location}
                                placeholder={strings.events.create.placeholders.location}
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={errors.locationString?.message}
                                autoCapitalize="sentences"
                                p="$3"
                                fos="$3"
                            />
                        )}
                    />

                    {/* Description Field */}
                    <Controller
                        control={control}
                        name="description"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CobuInput
                                label={strings.events.create.labels.description}
                                placeholder={strings.events.create.placeholders.description}
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={errors.description?.message}
                                autoCapitalize="sentences"
                                multiline
                                numberOfLines={3}
                                p="$3"
                                fos="$3"
                            />
                        )}
                    />

                    {/* What to Bring Field (Optional) */}
                    <Controller
                        control={control}
                        name="whatToBring"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CobuInput
                                label={strings.events.create.labels.whatToBring}
                                placeholder={strings.events.create.placeholders.whatToBring}
                                value={value || ''}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={errors.whatToBring?.message}
                                autoCapitalize="sentences"
                                multiline
                                numberOfLines={2}
                                p="$3"
                                fos="$3"
                            />
                        )}
                    />

                    {/* Max Attendees Field */}
                    <Controller
                        control={control}
                        name="maxEventAttendees"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CobuInput
                                label={strings.events.create.labels.totalAttendees}
                                placeholder={strings.events.create.placeholders.totalAttendees}
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={errors.maxEventAttendees?.message}
                                keyboardType="number-pad"
                                p="$3"
                                fos="$3"
                            />
                        )}
                    />

                    {/* Max Guests Field */}
                    <Controller
                        control={control}
                        name="maxGuests"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CobuInput
                                label={strings.events.create.labels.maxGuestsPerRSVP}
                                placeholder={strings.events.create.placeholders.maxGuests}
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={errors.maxGuests?.message}
                                keyboardType="number-pad"
                                editable={!!watchedValues.maxEventAttendees}
                                p="$3"
                                fos="$3"
                            />
                        )}
                    />

                    <CobuSpacer size="$6" />

                    <CobuStack gap="$3">
                        <CobuButton
                            variant="primary"
                            onPress={handleSubmit(onSubmit)}
                            disabled={isSubmitting}
                            o={(isSubmitting) ? 0.5 : 1}
                            title={isSubmitting
                                ? (isEditMode ? strings.events.detail.buttons.updating : strings.events.detail.buttons.creating)
                                : (isEditMode ? strings.events.detail.buttons.updateEvent : strings.events.detail.buttons.createEvent)}
                        />
                        <CobuSpacer size="$10" />
                    </CobuStack>
                </CobuStack>
            </ScrollView>
        </CobuContainer>
    );
}
